/**
 * Reading a country out of a phone number.
 *
 * This exists because of one shape of list. A LinkedIn export carries a name, a
 * title and a location, and the location is what settles the language. A
 * WhatsApp list carries a phone number and very often nothing else at all: no
 * location column, sometimes not even a full name, because the number *is* the
 * address on that channel. On a list like that the dialling code is not a weak
 * extra hint, it is the only fact about the person on the page.
 *
 * THE RULES, AND WHY EACH ONE IS A REFUSAL
 *
 * Every rule here exists to stop a number being read as a country it is not,
 * because the cost is asymmetric in exactly the way the rest of this directory
 * is built around: a number read wrong writes a message in a language the
 * person does not speak, and a number left unread writes English, which is
 * never actively wrong.
 *
 *   1. **A leading zero is a national number and carries no country.** `0812…`
 *      is how Indonesia writes its own mobile numbers, and it is also how the
 *      United Kingdom writes every one of theirs. There is no country in that
 *      string. This is the single most common shape in a locally-sourced list
 *      and the honest answer to it is silence, which is why the campaign's own
 *      language field exists: a list that is all one country is something
 *      somebody knows and can say once, rather than something a parser should
 *      pretend to discover per row.
 *
 *   2. **A number with no `+` is barely trusted at all.** It needs eleven
 *      digits (`4155551234` is an ordinary ten-digit San Francisco number whose
 *      first two digits are Switzerland's code), it must contain no letters (an
 *      extension or an order number otherwise reads as a country), and it must
 *      not begin 7, 8 or 9 (that is a national mobile whose leading zero a
 *      spreadsheet has eaten). The cost is real and worth naming: plus-less
 *      Singapore, Pakistani, Japanese, Korean and Chinese numbers are not read.
 *      They cost English, which is the safe answer.
 *
 *      A number that arrives with its `+`, which is what a WhatsApp export
 *      carries because the app itself requires it, has a stated country code
 *      rather than a guessed one, and none of these three rules applies to it.
 *
 *   3. **A shared dialling code that does not agree with itself is refused.**
 *      `+7` is Russia and Kazakhstan, and those two do not point at the same
 *      language. `+1` is the United States and Canada, which do. The caller is
 *      handed every candidate rather than a coin flip, and the language
 *      inference only acts when they agree.
 *
 * WHAT THIS TABLE IS NOT. It is not a phone number validator and it is not
 * libphonenumber. It answers one question, "which country issued this dialling
 * code", for the markets this platform writes to. It does not check that the
 * rest of the number is a real number, because it does not need to: an
 * unroutable number fails at the send, loudly, and guessing about its interior
 * would add nothing here.
 */

/** Why a number produced no single country. `ok` means it did. */
export type PhoneOriginOutcome =
  | 'ok'
  /** Parsed, but the dialling code belongs to more than one country. */
  | 'shared_code'
  /** A national number: a leading zero, or too short to be international. */
  | 'national_format'
  /** International-looking, but the leading digits are not a code we hold. */
  | 'unknown_code'
  /** Nothing usable: empty, or not enough digits to be a phone number. */
  | 'not_a_number'

export interface PhoneOrigin {
  /** The dialling code, written the way a person writes it: `+62`. */
  dialCode: string | null
  /** The one country it names. Null whenever the code is shared or unread. */
  country: string | null
  /** Every country sharing the code, so a caller can decide for itself. */
  countries: readonly string[]
  outcome: PhoneOriginOutcome
}

const NOTHING: PhoneOrigin = { dialCode: null, country: null, countries: [], outcome: 'not_a_number' }

/**
 * Dialling code to country, for the markets this platform writes to.
 *
 * Keyed by the digits without the `+`. Matched longest-first, so `+852` finds
 * Hong Kong before anything tries to read it as `+85`.
 *
 * The North American codes are the one place this goes deeper than one entry.
 * `+1` covers twenty-odd countries and almost all of them work in English, so
 * one entry for the pair that matters would normally be enough. The Dominican
 * Republic and Puerto Rico are the exceptions that would be read wrong, and
 * they are separable: both hold whole area codes of their own. Listing those
 * five costs nothing and stops a Spanish-speaking business being filed as
 * American. Neither is a market this platform holds a language rule for yet, so
 * today both still resolve to English. They resolve to English *knowing which
 * country they are*, which is the difference that matters when the table grows.
 */
const DIAL_CODES: Readonly<Record<string, readonly string[]>> = {
  // North America, most specific first in spirit; the matcher enforces it.
  '1809': ['DO'],
  '1829': ['DO'],
  '1849': ['DO'],
  '1787': ['PR'],
  '1939': ['PR'],
  '1': ['US', 'CA'],

  // Europe.
  '7': ['RU', 'KZ'],
  '30': ['GR'],
  '31': ['NL'],
  '32': ['BE'],
  '33': ['FR'],
  '34': ['ES'],
  '36': ['HU'],
  '39': ['IT'],
  '40': ['RO'],
  '41': ['CH'],
  '43': ['AT'],
  '44': ['GB'],
  '45': ['DK'],
  '46': ['SE'],
  '47': ['NO'],
  '48': ['PL'],
  '49': ['DE'],
  '351': ['PT'],
  '352': ['LU'],
  '353': ['IE'],
  '354': ['IS'],
  '356': ['MT'],
  '357': ['CY'],
  '358': ['FI'],
  '359': ['BG'],
  '370': ['LT'],
  '371': ['LV'],
  '372': ['EE'],
  '380': ['UA'],
  '381': ['RS'],
  '385': ['HR'],
  '386': ['SI'],
  '420': ['CZ'],
  '421': ['SK'],

  // The Americas below the top.
  '51': ['PE'],
  '52': ['MX'],
  '54': ['AR'],
  '55': ['BR'],
  '56': ['CL'],
  '57': ['CO'],

  // Asia and Oceania.
  '60': ['MY'],
  '61': ['AU'],
  '62': ['ID'],
  '63': ['PH'],
  '64': ['NZ'],
  '65': ['SG'],
  '66': ['TH'],
  '673': ['BN'],
  '81': ['JP'],
  '82': ['KR'],
  '84': ['VN'],
  '86': ['CN'],
  '852': ['HK'],
  '855': ['KH'],
  '880': ['BD'],
  '886': ['TW'],
  '90': ['TR'],
  '91': ['IN'],
  '92': ['PK'],
  '94': ['LK'],

  // Middle East and Africa.
  '20': ['EG'],
  '27': ['ZA'],
  '212': ['MA'],
  '213': ['DZ'],
  '216': ['TN'],
  '233': ['GH'],
  '234': ['NG'],
  '254': ['KE'],
  '961': ['LB'],
  '962': ['JO'],
  '965': ['KW'],
  '966': ['SA'],
  '968': ['OM'],
  '971': ['AE'],
  '973': ['BH'],
  '974': ['QA'],
}

/** The longest code in the table, so the matcher knows where to start. */
const LONGEST_CODE = Object.keys(DIAL_CODES).reduce((longest, code) => Math.max(longest, code.length), 0)

/**
 * The shortest number this reads as international when it arrives without a `+`.
 *
 * Eleven, because ten is a domestic North American number and those are common
 * in every list. See rule 2 in the file header for what this costs.
 */
const PLUSLESS_INTERNATIONAL_MIN_DIGITS = 11

/**
 * Where a phone number says its owner is.
 *
 * Never throws. Every failure is a named outcome rather than an exception,
 * because "this number tells us nothing" is an ordinary answer that a screen
 * needs to be able to explain.
 */
export function phoneOrigin(phone: string | null | undefined): PhoneOrigin {
  const raw = (phone ?? '').trim()
  if (raw === '') return NOTHING

  const hadPlus = raw.startsWith('+')
  const digits = raw.replace(/\D+/g, '')
  if (digits.length < 7 || digits.length > 15) return NOTHING

  // `00` is the other way to write `+`, and it is what half of Europe types.
  // Checked before the leading-zero rule, or every `0049…` reads as national.
  const international = hadPlus ? digits : digits.startsWith('00') ? digits.slice(2) : null

  if (international === null) {
    // A leading zero is a trunk prefix. There is no country in the string.
    if (digits.startsWith('0')) {
      return { dialCode: null, country: null, countries: [], outcome: 'national_format' }
    }
    if (digits.length < PLUSLESS_INTERNATIONAL_MIN_DIGITS) {
      return { dialCode: null, country: null, countries: [], outcome: 'national_format' }
    }
    // A plus-less read needs the text to be nothing but a number.
    //
    // Without the `+` the leading digits are the only evidence there is, and
    // that evidence is destroyed by anything else in the cell. "206-555-0100
    // ext 12" is a Seattle number with an extension; strip the punctuation and
    // it is twelve digits beginning 20, which is Egypt. "order 4400123456789"
    // is not a phone number at all and reads as the United Kingdom. Both were
    // measured, both would have filled a country column and written a message
    // in the wrong language, and both contain a letter that says plainly this
    // cell is not one number.
    //
    // With a `+` the country code is stated rather than inferred, so trailing
    // prose cannot move it and the same extension resolves correctly.
    if (/[a-z]/i.test(raw)) {
      return { dialCode: null, country: null, countries: [], outcome: 'not_a_number' }
    }
    // A plus-less number beginning 7, 8 or 9 is refused, because that is what a
    // national mobile looks like once a spreadsheet has eaten its leading zero.
    //
    // Measured, and it is the likeliest way this whole feature gets a list
    // wrong: Indonesian mobiles are all `08…`, Excel treats a phone column as a
    // number and drops the zero, and `081234567890` arrives as `81234567890`,
    // which is eleven digits beginning 81, which is Japan. Japanese is
    // unreviewable, so the draft comes out in English and the country column
    // says JP. The same strip turns every British `07…` into `7…`, which is
    // Russia and Kazakhstan.
    //
    // The trade, stated rather than hidden: a genuinely plus-less Pakistani
    // (`92…`), Japanese, Korean, Chinese or Vietnamese (`8…`) number is no
    // longer read either. Those are false negatives and they cost English,
    // which is the safe answer. Reading a stripped Indonesian mobile as
    // Japanese costs the language the list was built for.
    //
    // Nothing here applies to a number carrying its `+`, where the country code
    // is stated rather than guessed at.
    if (/^[789]/.test(digits)) {
      return { dialCode: null, country: null, countries: [], outcome: 'national_format' }
    }
    return lookUp(digits)
  }

  if (international.length < 7) return NOTHING
  return lookUp(international)
}

function lookUp(digits: string): PhoneOrigin {
  for (let length = Math.min(LONGEST_CODE, digits.length - 1); length >= 1; length -= 1) {
    const code = digits.slice(0, length)
    const countries = DIAL_CODES[code]
    if (countries === undefined) continue
    return {
      dialCode: `+${code}`,
      country: countries.length === 1 ? (countries[0] as string) : null,
      countries,
      outcome: countries.length === 1 ? 'ok' : 'shared_code',
    }
  }
  return { dialCode: null, country: null, countries: [], outcome: 'unknown_code' }
}

/**
 * The one country a number names, or null.
 *
 * The convenience form, for the callers that only want to fill a column. Anyone
 * who needs to *explain* the answer wants `phoneOrigin` instead.
 */
export function countryFromPhone(phone: string | null | undefined): string | null {
  return phoneOrigin(phone).country
}

/**
 * How a person would say where the number is from: "an Indonesian number".
 *
 * Null when the number names no single country, so a caller can fall through to
 * its own wording rather than printing "a null number".
 */
export function describePhoneOrigin(origin: PhoneOrigin, countryName: (code: string) => string): string | null {
  if (origin.country === null || origin.dialCode === null) return null
  return `their number is ${origin.dialCode}, which is ${countryName(origin.country)}`
}
